import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Lock, Loader2, CheckCircle, XCircle, ArrowRight } from "lucide-react";

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token"); // URL'den token'ı alıyoruz
  const API_URL = process.env.REACT_APP_API_URL;

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [status, setStatus] = useState("idle"); // idle, loading, success, error
  const [message, setMessage] = useState("");

  useEffect(() => {
    if (!token) {
      setStatus("error");
      setMessage("Geçersiz veya eksik bağlantı. Lütfen tekrar deneyin.");
    }
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (password !== confirmPassword) {
      setMessage("Şifreler birbiriyle uyuşmuyor.");
      setStatus("error");
      return;
    }
    
    if (password.length < 6) {
        setMessage("Şifre en az 6 karakter olmalıdır.");
        setStatus("error");
        return;
    }

    setStatus("loading");
    setMessage("");

    try {
      const res = await fetch(`${API_URL}/reset-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, new_password: password }),
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.detail || "Şifre sıfırlama başarısız.");
      }

      setStatus("success");
    } catch (err) {
      setStatus("error");
      setMessage(err.message);
    }
  };

  return (
    <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-gray-900 to-gray-800 p-4 font-sans text-gray-50">
      <div className="w-full max-w-md bg-gray-800 p-8 rounded-3xl shadow-2xl border border-gray-700">
        
        {status === "success" ? (
          <div className="text-center space-y-6 animate-in fade-in zoom-in duration-300">
            <div className="mx-auto w-20 h-20 bg-green-900/30 rounded-full flex items-center justify-center border border-green-500/50">
              <CheckCircle className="h-10 w-10 text-green-400" />
            </div>
            <h2 className="text-2xl font-bold text-white">Şifreniz Güncellendi!</h2>
            <p className="text-gray-400">
              Şifreniz başarıyla değiştirildi. Artık yeni şifrenizle giriş yapabilirsiniz.
            </p>
            <button
              onClick={() => navigate("/login")}
              className="w-full flex items-center justify-center gap-2 bg-green-600 hover:bg-green-700 text-white py-3 rounded-lg font-semibold transition-colors"
            >
              Giriş Yap <ArrowRight className="h-4 w-4" />
            </button>
          </div>
        ) : (
          <>
            <header className="text-center mb-8">
              <div className="flex items-center justify-center gap-3 mb-2">
                <Lock className="h-10 w-10 text-blue-400" />
                <h2 className="text-2xl font-bold text-gray-50">Yeni Şifre Belirle</h2>
              </div>
              <p className="text-gray-400 mt-2 text-sm">
                Hesabın için yeni ve güçlü bir şifre oluştur.
              </p>
            </header>

            {status === "error" && (
              <div className="flex items-center gap-3 p-3 mb-6 text-red-400 bg-red-900/30 rounded-xl border border-red-700 text-sm">
                <XCircle className="h-5 w-5 flex-shrink-0" />
                <span>{message}</span>
              </div>
            )}

            {token ? (
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-300">Yeni Şifre</label>
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    className="w-full px-4 py-3 bg-gray-700 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 border border-gray-600"
                    placeholder="******"
                  />
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-300">Yeni Şifre (Tekrar)</label>
                  <input
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    className="w-full px-4 py-3 bg-gray-700 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 border border-gray-600"
                    placeholder="******"
                  />
                </div>

                <button
                  type="submit"
                  disabled={status === "loading"}
                  className={`w-full flex items-center justify-center gap-2 px-6 py-3 font-semibold rounded-lg transition-colors 
                    ${status === "loading" ? 'bg-blue-800 text-blue-200 cursor-not-allowed' : 'bg-blue-600 text-white hover:bg-blue-700'}`}
                >
                  {status === "loading" ? (
                    <> <Loader2 className="h-5 w-5 animate-spin" /> Güncelleniyor... </>
                  ) : (
                    "Şifreyi Güncelle"
                  )}
                </button>
              </form>
            ) : (
               <div className="text-center">
                 <p className="text-red-400 mb-4">Token bulunamadı.</p>
                 <button onClick={() => navigate("/login")} className="text-blue-400 hover:underline">Giriş sayfasına dön</button>
               </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}